<script lang="ts" setup>
import { computed } from "vue";
import { useTranslations } from "@/composables/useTranslations";

const { t } = useTranslations();

const props = defineProps<{
    totalDomains: number;
    totalActiveDomains: number;
    totalInactiveDomains: number;
    emptyDomainsSlots: number;
    totalEmailsReported: number;
    totalParkedDomainsCount: number;
}>();

const tt = computed(() => ({
    domainsSlots: t("vue.domains_slots_tooltip"),
    activeDomains: t("vue.active_domains_tooltip"),
    inactiveDomains: t("vue.inactive_domains_tooltip"),
    emptySlots: t("vue.empty_slots_tooltip"),
    parkedDomains: t("vue.parked_domains_tooltip"),
}));
</script>

<template>
    <div
        class="grid w-full grid-cols-1 gap-[21px] sm:grid-cols-2 lg:grid-cols-5"
    >
        <div class="rounded-[12.8px] border border-gray-200 bg-white p-[21px]">
            <div class="py-[3px]">
                <div
                    class="inline-flex items-center gap-1 text-[13px] leading-[18px] text-gray-600"
                >
                    {{ t("vue.domains_slots") }}

                    <span class="relative inline-flex group">
                        <button
                            type="button"
                            class="inline-flex h-[18px] w-[18px] items-center justify-center border-0 bg-transparent p-0 cursor-default focus:outline-none"
                            :aria-label="tt.domainsSlots"
                            tabindex="0"
                        >
                            <svg
                                viewBox="0 0 20 20"
                                aria-hidden="true"
                                class="h-4 w-4 text-gray-400 fill-none stroke-current stroke-[1.5]"
                            >
                                <circle cx="10" cy="10" r="9"></circle>
                                <path d="M10 15v-5"></path>
                                <circle cx="10" cy="6.5" r="1"></circle>
                            </svg>
                        </button>

                        <span
                            class="pointer-events-none absolute left-1/2 top-full z-10 mt-[10px] min-w-[100px] max-w-[120px] -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-[10px] text-center text-sm font-medium leading-tight !text-white whitespace-normal opacity-0 shadow-[0_8px_24px_rgba(0,0,0,0.18)] transition duration-150 ease-out group-hover:opacity-100 group-hover:translate-y-[2px] group-focus-within:opacity-100 group-focus-within:translate-y-[2px] max-sm:w-[200px] max-sm:max-w-[200px]"
                        >
                            <span
                                class="absolute left-1/2 top-0 h-0 w-0 -translate-x-1/2 -translate-y-full border-x-[7px] border-x-transparent border-b-[7px] border-b-gray-900"
                            ></span>

                            {{ tt.domainsSlots }}
                        </span>
                    </span>
                </div>

                <div
                    class="mt-1 text-[21px] font-bold leading-[1.1] text-gray-900"
                >
                    {{ totalDomains }}
                </div>
            </div>
        </div>

        <div class="rounded-[12.8px] border border-gray-200 bg-white p-[21px]">
            <div class="py-[3px]">
                <div
                    class="inline-flex items-center gap-1 text-[13px] leading-[18px] text-gray-600"
                >
                    {{ t("vue.active_domains") }}

                    <span class="relative inline-flex group">
                        <button
                            type="button"
                            class="inline-flex h-[18px] w-[18px] items-center justify-center border-0 bg-transparent p-0 cursor-default focus:outline-none"
                            :aria-label="tt.activeDomains"
                            tabindex="0"
                        >
                            <svg
                                viewBox="0 0 20 20"
                                aria-hidden="true"
                                class="h-4 w-4 text-gray-400 fill-none stroke-current stroke-[1.5]"
                            >
                                <circle cx="10" cy="10" r="9"></circle>
                                <path d="M10 15v-5"></path>
                                <circle cx="10" cy="6.5" r="1"></circle>
                            </svg>
                        </button>

                        <span
                            class="pointer-events-none absolute left-1/2 top-full z-10 mt-[10px] min-w-[100px] max-w-[120px] -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-[10px] text-center text-sm font-medium leading-tight !text-white whitespace-normal opacity-0 shadow-[0_8px_24px_rgba(0,0,0,0.18)] transition duration-150 ease-out group-hover:opacity-100 group-hover:translate-y-[2px] group-focus-within:opacity-100 group-focus-within:translate-y-[2px] max-sm:w-[200px] max-sm:max-w-[200px]"
                        >
                            <span
                                class="absolute left-1/2 top-0 h-0 w-0 -translate-x-1/2 -translate-y-full border-x-[7px] border-x-transparent border-b-[7px] border-b-gray-900"
                            />
                            {{ tt.activeDomains }}
                        </span>
                    </span>
                </div>

                <div
                    class="mt-1 text-[21px] font-bold leading-[1.1] text-gray-900"
                >
                    {{ totalActiveDomains }}
                </div>
            </div>
        </div>

        <div class="rounded-[12.8px] border border-gray-200 bg-white p-[21px]">
            <div class="py-[3px]">
                <div
                    class="inline-flex items-center gap-1 text-[13px] leading-[18px] text-gray-600"
                >
                    {{ t("vue.inactive_domains") }}

                    <span class="relative inline-flex group">
                        <button
                            type="button"
                            class="inline-flex h-[18px] w-[18px] items-center justify-center border-0 bg-transparent p-0 cursor-default focus:outline-none"
                            :aria-label="tt.inactiveDomains"
                            tabindex="0"
                        >
                            <svg
                                viewBox="0 0 20 20"
                                aria-hidden="true"
                                class="h-4 w-4 text-gray-400 fill-none stroke-current stroke-[1.5]"
                            >
                                <circle cx="10" cy="10" r="9"></circle>
                                <path d="M10 15v-5"></path>
                                <circle cx="10" cy="6.5" r="1"></circle>
                            </svg>
                        </button>

                        <span
                            class="pointer-events-none absolute left-1/2 top-full z-10 mt-[10px] min-w-[100px] max-w-[120px] -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-[10px] text-center text-sm font-medium leading-tight !text-white whitespace-normal opacity-0 shadow-[0_8px_24px_rgba(0,0,0,0.18)] transition duration-150 ease-out group-hover:opacity-100 group-hover:translate-y-[2px] group-focus-within:opacity-100 group-focus-within:translate-y-[2px] max-sm:w-[200px] max-sm:max-w-[200px]"
                        >
                            <span
                                class="absolute left-1/2 top-0 h-0 w-0 -translate-x-1/2 -translate-y-full border-x-[7px] border-x-transparent border-b-[7px] border-b-gray-900"
                            />
                            {{ tt.inactiveDomains }}
                        </span>
                    </span>
                </div>

                <div
                    class="mt-1 text-[21px] font-bold leading-[1.1] text-gray-900"
                >
                    {{ totalInactiveDomains }}
                </div>
            </div>
        </div>

        <div class="rounded-[12.8px] border border-gray-200 bg-white p-[21px]">
            <div class="py-[3px]">
                <div
                    class="inline-flex items-center gap-1 text-[13px] leading-[18px] text-gray-600"
                >
                    {{ t("vue.parked_domains") }}

                    <span class="relative inline-flex group">
                        <button
                            type="button"
                            class="inline-flex h-[18px] w-[18px] items-center justify-center border-0 bg-transparent p-0 cursor-default focus:outline-none"
                            :aria-label="tt.parkedDomains"
                            tabindex="0"
                        >
                            <svg
                                viewBox="0 0 20 20"
                                aria-hidden="true"
                                class="h-4 w-4 text-gray-400 fill-none stroke-current stroke-[1.5]"
                            >
                                <circle cx="10" cy="10" r="9"></circle>
                                <path d="M10 15v-5"></path>
                                <circle cx="10" cy="6.5" r="1"></circle>
                            </svg>
                        </button>

                        <span
                            class="pointer-events-none absolute left-1/2 top-full z-10 mt-[10px] min-w-[100px] max-w-[120px] -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-[10px] text-center text-sm font-medium leading-tight !text-white whitespace-normal opacity-0 shadow-[0_8px_24px_rgba(0,0,0,0.18)] transition duration-150 ease-out group-hover:opacity-100 group-hover:translate-y-[2px] group-focus-within:opacity-100 group-focus-within:translate-y-[2px] max-sm:w-[200px] max-sm:max-w-[200px]"
                        >
                            <span
                                class="absolute left-1/2 top-0 h-0 w-0 -translate-x-1/2 -translate-y-full border-x-[7px] border-x-transparent border-b-[7px] border-b-gray-900"
                            />
                            {{ tt.parkedDomains }}
                        </span>
                    </span>
                </div>

                <div
                    class="mt-1 text-[21px] font-bold leading-[1.1] text-gray-900"
                >
                    {{ totalParkedDomainsCount }}
                </div>
            </div>
        </div>

        <div class="rounded-[12.8px] border border-gray-200 bg-white p-[21px]">
            <div class="py-[3px]">
                <div
                    class="inline-flex items-center gap-1 text-[13px] leading-[18px] text-gray-600"
                >
                    {{ t("vue.empty_slots") }}

                    <span class="relative inline-flex group">
                        <button
                            type="button"
                            class="inline-flex h-[18px] w-[18px] items-center justify-center border-0 bg-transparent p-0 cursor-default focus:outline-none"
                            :aria-label="tt.emptySlots"
                            tabindex="0"
                        >
                            <svg
                                viewBox="0 0 20 20"
                                aria-hidden="true"
                                class="h-4 w-4 text-gray-400 fill-none stroke-current stroke-[1.5]"
                            >
                                <circle cx="10" cy="10" r="9"></circle>
                                <path d="M10 15v-5"></path>
                                <circle cx="10" cy="6.5" r="1"></circle>
                            </svg>
                        </button>

                        <span
                            class="pointer-events-none absolute left-1/2 top-full z-10 mt-[10px] min-w-[100px] max-w-[120px] -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-[10px] text-center text-sm font-medium leading-tight !text-white whitespace-normal opacity-0 shadow-[0_8px_24px_rgba(0,0,0,0.18)] transition duration-150 ease-out group-hover:opacity-100 group-hover:translate-y-[2px] group-focus-within:opacity-100 group-focus-within:translate-y-[2px] max-sm:w-[200px] max-sm:max-w-[200px]"
                        >
                            <span
                                class="absolute left-1/2 top-0 h-0 w-0 -translate-x-1/2 -translate-y-full border-x-[7px] border-x-transparent border-b-[7px] border-b-gray-900"
                            />
                            {{ tt.emptySlots }}
                        </span>
                    </span>
                </div>

                <div
                    class="mt-1 text-[21px] font-bold leading-[1.1] text-gray-900"
                >
                    {{ emptyDomainsSlots }}
                </div>
            </div>
        </div>
    </div>
</template>
